import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { EditionDocumentation } from "../../../../api/graphql";
import { EditionDocumentationListStore } from "../../store";
import { musicalWorkDisplayId } from "../../../musicalWork/store/MusicalWorkCommonStore";
import { territoryDisplayId } from "../../../territory/store/TerritoryCommonStore";
import { authorComposerDisplayId } from "../../../authorComposer/store/AuthorComposerCommonStore";
import { editionContractPublisherDisplayId } from "../../../editionContractPublisher/store/EditionContractPublisherCommonStore";

import { VisibilityIcon } from "../../../../icons";

export type GridExpressProps = { listStore?: EditionDocumentationListStore };

export const EditionDocumentationListForm: React.FC<GridExpressProps> =
  observer(({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<EditionDocumentation>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: EditionDocumentation, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<EditionDocumentation>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "works") {
          return {
            ...col,
            id: "works",
            accessorFn: (row: EditionDocumentation) =>
              row.works?.length
                ? row.works
                    .map((x: any) => musicalWorkDisplayId(x, langStore))
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "territories") {
          return {
            ...col,
            id: "territories",
            accessorFn: (row: EditionDocumentation) =>
              row.territories?.length
                ? row.territories
                    .map((x: any) => territoryDisplayId(x, langStore))
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "authors") {
          return {
            ...col,
            id: "authors",
            accessorFn: (row: EditionDocumentation) =>
              row.authors?.length
                ? row.authors
                    .map((x: any) => authorComposerDisplayId(x, langStore))
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "publishers") {
          return {
            ...col,
            id: "publishers",
            accessorFn: (row: EditionDocumentation) =>
              row.publishers?.length
                ? row.publishers
                    .map((x: any) =>
                      editionContractPublisherDisplayId(x, langStore)
                    )
                    .join("\n")
                : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.editionDocumentationCurrentPage || [];

    return (
      <div id={"ListOfEditionDocumentation"}>
        <div id={"StartOfListingOfEditionDocumentation"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  });
